/**
 * MCP リソース / プロンプト スキーマ
 *
 * MCP サーバーが公開する resources / prompts の非シークレット射影を定義する。
 * 認証情報の実値・`secret-ref` 参照・環境変数値は一切含めない（Zod 既定の strip により
 * 想定外フィールドは射影の時点で落ちる）。
 *
 * 全型が `serverName` を必須で保持し、複数サーバーの結果を統合しても由来が失われない。
 */
import { z } from 'zod';
// ========================================
// ツール種別
// ========================================
/**
 * MCP ツールの種別。
 *
 * - `server`: MCP サーバーが提供する実ツール
 * - `resource-read`: リソース読み取りのためにクライアント側で合成したツール
 *
 * 利用側の型では optional とし、省略時は `server` として扱う（後方互換）。
 */
export const MCPToolKindSchema = z.enum(['server', 'resource-read']);
// ========================================
// リソース
// ========================================
export const MCPResourceInfoSchema = z.object({
    uri: z.string(),
    name: z.string(),
    title: z.string().optional(),
    description: z.string().optional(),
    mimeType: z.string().optional(),
    size: z.number().optional(),
    serverName: z.string(),
});
export const MCPResourceTemplateInfoSchema = z.object({
    uriTemplate: z.string(),
    name: z.string(),
    title: z.string().optional(),
    description: z.string().optional(),
    mimeType: z.string().optional(),
    serverName: z.string(),
});
/**
 * リソース内容の判別可能ユニオン。
 *
 * `binary` は本文を持たずメタ情報のみを保持する。SDK 生スキーマの `blob`（base64）は
 * 射影の時点で捨てられるため、バイナリ本体が LLM のコンテキストへ流れる経路が型で塞がれる。
 */
export const MCPTextResourceContentSchema = z.object({
    kind: z.literal('text'),
    uri: z.string(),
    mimeType: z.string().optional(),
    text: z.string(),
});
export const MCPBinaryResourceContentSchema = z.object({
    kind: z.literal('binary'),
    uri: z.string(),
    mimeType: z.string().optional(),
    byteLength: z.number().optional(),
});
export const MCPResourceContentSchema = z.discriminatedUnion('kind', [
    MCPTextResourceContentSchema,
    MCPBinaryResourceContentSchema,
]);
export const MCPResourceReadResultSchema = z.object({
    serverName: z.string(),
    uri: z.string(),
    contents: z.array(MCPResourceContentSchema),
});
// ========================================
// プロンプト
// ========================================
export const MCPPromptArgumentInfoSchema = z.object({
    name: z.string(),
    description: z.string().optional(),
    required: z.boolean().optional(),
});
export const MCPPromptInfoSchema = z.object({
    name: z.string(),
    title: z.string().optional(),
    description: z.string().optional(),
    arguments: z.array(MCPPromptArgumentInfoSchema).optional(),
    serverName: z.string(),
});
export const MCPPromptMessageRoleSchema = z.enum(['user', 'assistant']);
/**
 * プロンプトメッセージ。
 *
 * `content.type` はテキスト以外（image / audio / resource / resource_link）を取りうるため
 * 文字列のまま保持し、`text` は text パートでのみ存在する。
 */
export const MCPPromptMessageSchema = z.object({
    role: MCPPromptMessageRoleSchema,
    content: z.object({
        type: z.string(),
        text: z.string().optional(),
    }),
});
export const MCPPromptResultSchema = z.object({
    serverName: z.string(),
    name: z.string(),
    description: z.string().optional(),
    messages: z.array(MCPPromptMessageSchema),
});
//# sourceMappingURL=mcp-schemas.js.map